// ============================================
// pages/EmbedPreview.tsx
// 审核预览精简页 (给后台审核抽屉 iframe 嵌入用).
// URL: /embed/:shortId?token=<adminToken>
// 只渲染: 作品图 + 积木 + 代码. 无顶栏/无作者/无其他作品/无任何跳转入口.
// 通过 URL token 让后端识别 admin, 从而能看 pending_audit 作品.
// ============================================

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import BlocklyViewer from '../components/BlocklyViewer';
import { callApi } from '../services/api';
import type { ProjectPublic } from '../types/community';

export default function EmbedPreview() {
  const { shortId } = useParams<{ shortId: string }>();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [project, setProject] = useState<ProjectPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'blocks' | 'code'>('blocks');

  useEffect(() => {
    if (!shortId) return;
    setLoading(true);
    // 带 admin token 调 getProject(后端 requireAdmin 识别 → 可看审核中作品)
    callApi<{ project: ProjectPublic }>(
      'community-get-project',
      { short_id: shortId, token },
      { noAuth: true }, // 不走普通用户登录态, token 显式放 payload
    )
      .then((r) => {
        if (r.ok && r.project) setProject(r.project as ProjectPublic);
        else setError(r.message || r.error || '加载失败');
      })
      .catch(() => setError('加载失败'))
      .finally(() => setLoading(false));
  }, [shortId, token]);

  if (loading) {
    return <div className="p-8 text-center text-ink-400 text-sm">加载中…</div>;
  }
  if (error || !project) {
    return <div className="p-8 text-center text-red-600 text-sm">{error || '作品不存在'}</div>;
  }

  return (
    <div className="bg-white min-h-screen p-4 space-y-5">
      {/* 标题 + 状态 */}
      <div>
        <h1 className="text-lg font-bold text-ink-900">{project.title}</h1>
        <div className="mt-1 flex items-center gap-2 text-xs">
          <StatusBadge status={project.status} />
          {project.board_type && <span className="text-ink-400">{project.board_type}</span>}
          {typeof project.block_count === 'number' && (
            <span className="text-ink-400">· {project.block_count} 积木</span>
          )}
        </div>
        {project.description && (
          <p className="mt-2 text-sm text-ink-600 whitespace-pre-wrap">{project.description}</p>
        )}
      </div>

      {/* 作品图 */}
      {project.cover_url && (
        <div className="rounded-lg overflow-hidden border border-ink-300/40">
          <img src={project.cover_url} alt="作品封面" className="w-full object-cover" />
        </div>
      )}

      {/* 积木 / 代码 切换 */}
      <div>
        <div className="flex gap-1 mb-2">
          <TabBtn active={tab === 'blocks'} onClick={() => setTab('blocks')}>积木</TabBtn>
          <TabBtn active={tab === 'code'} onClick={() => setTab('code')}>代码</TabBtn>
        </div>

        {tab === 'blocks' ? (
          <BlocklyViewer xml={project.workspace_xml || null} boardType={project.board_type} />
        ) : (
          <pre className="bg-[#1e1e1e] text-[#d4d4d4] text-xs leading-relaxed rounded-lg p-4 overflow-auto max-h-[460px] whitespace-pre-wrap">
            {project.code || '(无代码)'}
          </pre>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
        active ? 'bg-gold-600 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
      }`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    pending_audit: { label: '审核中', cls: 'bg-amber-100 text-amber-800' },
    published: { label: '已发布', cls: 'bg-green-100 text-green-800' },
    rejected: { label: '已拒绝', cls: 'bg-red-100 text-red-800' },
    hidden: { label: '已隐藏', cls: 'bg-ink-200 text-ink-600' },
  };
  const s = map[status] || { label: status, cls: 'bg-ink-100 text-ink-600' };
  return <span className={`px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
}
